/**
 * Qué se puede TECLEAR en cada campo. Distinto de `validacion.js`, que dice qué falta.
 *
 * Son dos cosas y por eso están en dos archivos:
 *
 *   · `validacion.js` responde "¿se puede enviar?" y avisa DESPUÉS, junto al campo.
 *   · esto responde "¿esa tecla entra?" y actúa MIENTRAS se escribe.
 *
 * Se filtra en el `onChange` y no con `pattern` ni con el teclado (`onKeyDown`), a
 * propósito: `pattern` no impide escribir —solo marca el campo como inválido— y
 * bloquear teclas deja pasar el PEGADO, que es justo como llega la mitad de la basura
 * (un teléfono copiado de WhatsApp con un espacio invisible delante). Filtrando el
 * valor entero en cada cambio, teclear y pegar quedan cubiertos por el mismo camino.
 *
 * Todas las funciones son puras y toleran `undefined`: el estado del formulario nace
 * con cadenas vacías, pero un borrador retomado puede traer nulos del API.
 *
 * EL TOPE LLEGA POR PARÁMETRO, NUNCA ESCRITO AQUÍ. Los topes viven en `parametro`
 * (HU-31) y los sirve `/referencias/configuracion-del-formulario`; si llega
 * `undefined` —configuración no disponible— no se recorta nada, que es como se
 * comportaba el formulario antes de que los topes existieran.
 */

/**
 * LA FIRMA DE TODOS LOS SANEADORES DE ESTE MODULO.
 *
 * `valor: unknown` porque lo que llega es `e.target.value` de un input, y a veces el
 * valor ya guardado, que puede ser numero o nulo. `tope` es opcional a proposito: los
 * topes viven en `parametro` (HU-31) y cuando la configuracion no esta disponible no
 * se recorta nada, igual que antes de que los topes existieran.
 */
export type Saneador = (valor: unknown, tope?: number) => string;

const texto = (valor: unknown): string => String(valor ?? '');

/** Recorta al tope. Sin tope, devuelve el valor tal cual. */
export const recortar: Saneador = (valor, tope) => {
  const v = texto(valor);
  return tope ? v.slice(0, tope) : v;
};

/**
 * Teléfono: dígitos y un `+` de prefijo internacional. NADA MÁS.
 *
 * ⚠ ESTO ERA MÁS PERMISIVO HASTA EL 28-ago-2026 (`soloTelefono`): conservaba espacios,
 * paréntesis, guiones y puntos "porque así los escribe la gente". El equipo lo probó en
 * dev y devolvió la observación contraria (T-44 §1): el campo acaba guardando el MISMO
 * número escrito de seis maneras —`(01) 234-5678`, `01 234 5678`, `01.234.5678`— y
 * entonces no se puede comparar, ni buscar, ni deduplicar por notificante. Un teléfono
 * es una secuencia de dígitos; los separadores son maquetación, y la maquetación no se
 * guarda en la columna que el Comité usa para volver a llamar.
 *
 * CP-02-11 sigue cumpliéndose —el alcance son 14 mercados y no se fuerza el formato
 * peruano—: lo que sobrevive es justo el prefijo internacional. `+56 9 8765 4321` entra
 * como `+56987654321`, que es el mismo número chileno sin la decoración.
 *
 * El `+` solo cuenta si aparece ANTES del primer dígito: es un prefijo, no un signo
 * suelto en medio. Así, pegar `Cel: +51 987 654 321 (casa)` —el caso real, copiado de
 * WhatsApp— conserva el prefijo aunque venga precedido de texto.
 */
export const telefono: Saneador = (valor, tope) => {
  const crudo = texto(valor);
  const prefijo = /^[^\d+]*\+/.test(crudo) ? '+' : '';
  // `replaceAll` y no `replace` con `/g`: dice en el nombre que se sustituyen TODAS
  // las apariciones, que es justo lo que este filtro hace y lo que un lector rapido
  // se juega al confundirlo con el `replace` de una sola.
  return recortar(prefijo + crudo.replaceAll(/\D/g, ''), tope);
};

/** Solo dígitos, sin signo ni decimales. Para edades y conteos. */
export const soloEnteros: Saneador = (valor, tope) => recortar(texto(valor).replaceAll(/\D/g, ''), tope);

/**
 * Número con decimales. Para el peso.
 *
 * Acepta coma o punto porque el teclado numérico del móvil da coma en teclado español,
 * y se NORMALIZA a punto: dejar las dos conviviendo produce "3,5" y "3.5" guardados en
 * la misma columna, que no se pueden ordenar ni sumar. Un solo separador, el primero.
 *
 * EL TOPE CUENTA DÍGITOS, NO CARACTERES, y no es un capricho: el parámetro se llama
 * `animal_peso_digitos` y el contador de la pantalla dice "dígitos". Con `recortar` a
 * secas, el punto gastaría presupuesto y "1234.5" ocuparía seis de seis en un campo que
 * promete seis cifras. El punto no cuenta; las cifras sí.
 */
export const pesoDecimal: Saneador = (valor, tope) => {
  const limpio = texto(valor)
    .replaceAll(',', '.')
    .replaceAll(/[^\d.]/g, '')
    .replace(/^\.+/, '');   // sin parte entera no hay decimal que separar

  let salida = '';
  let digitos = 0;
  let separado = false;
  for (const caracter of limpio) {
    if (caracter === '.') {
      // Un solo separador, y nunca al principio: los siguientes se descartan y sus
      // cifras se pegan a las de la primera parte decimal ("4.2.5" -> "4.25").
      // Tampoco cuando ya no caben mas cifras: un "123456." colgando de un tope
      // agotado es un separador que no va a separar nada.
      if (separado || !salida || (tope && digitos >= tope)) continue;
      separado = true;
      salida += '.';
      continue;
    }
    if (tope && digitos >= tope) break;
    salida += caracter;
    digitos += 1;
  }
  return salida;
};

/**
 * Letras, espacios y los signos que llevan los nombres propios. Sin números.
 *
 * Se admiten tildes, ñ y diéresis —"Bretón", "Pequeño Münsterländer" son razas reales—
 * y el guion y el apóstrofo por lo mismo. Lo que se va son cifras y símbolos.
 *
 * EL PUNTO ENTRA DESDE EL 28-ago-2026 (T-44 §1). Este filtro pasó a cubrir también los
 * nombres de personas —notificante y dueño—, y ahí el punto no es decoración: "Dr.",
 * "Sra.", "J. M. Torres". Sin él, el campo mutilaba la abreviatura mientras se escribía
 * y el registrador veía desaparecer un carácter que no había hecho nada malo.
 */
export const soloLetras: Saneador = (valor, tope) =>
  recortar(texto(valor).replaceAll(/[^\p{L}\s.'’-]/gu, ''), tope);

/**
 * El nombre de UNA PERSONA, con los espacios raros normalizados.
 *
 * Es `soloLetras` más una vuelta imprescindible: cualquier blanco que no sea el espacio
 * normal —el espacio duro U+00A0 que deja Outlook o el directorio al copiar, el
 * tabulador, un salto de línea pegado— pasa a espacio normal ANTES de filtrar.
 *
 * Sin eso el filtro lo deja entrar (la clase `\s` los admite todos) y el validador lo
 * rechaza después: tanto el del front (`SIGNOS_DE_UN_NOMBRE`) como el del servidor
 * (`_SIGNOS_DE_UN_NOMBRE` en `evaluacion/dominio/invitacion.py`) solo aceptan U+0020.
 * El usuario leía «solo admite letras, espacios, guiones…» señalando algo que EN
 * PANTALLA es un espacio, y no había forma de corregirlo mirando (revisión de UX,
 * 22-sep-2026). Acusar de algo invisible es el peor error posible.
 */
export const soloNombreDePersona: Saneador = (valor, tope) =>
  recortar(texto(valor).replaceAll(/\s/gu, ' ').replaceAll(/[^\p{L} .'’-]/gu, ''), tope);

/**
 * Iniciales de la persona afectada: letras y puntos, en mayúscula.
 *
 * RNF-02, minimización: se piden iniciales y no el nombre. Filtrar a letras y puntos
 * es lo que impide que alguien escriba el nombre completo en el campo pensado para no
 * tenerlo — la minimización deja de depender de que el usuario se acuerde.
 */
export const soloIniciales: Saneador = (valor, tope) =>
  recortar(texto(valor).replaceAll(/[^\p{L}.]/gu, '').toUpperCase(), tope);

/**
 * Correo: lo que puede formar parte de una dirección, sin espacios ni acentos.
 *
 * Filtra mientras se escribe lo que el validador rechazaría después: espacios, tildes,
 * comillas, paréntesis, corchetes, `:;<>` y la coma. Al pegar, además, se limpia el
 * espacio de los extremos — es el error más común y el usuario no lo ve.
 *
 * NO se pasa a minúsculas aquí: hacerlo mientras se teclea mueve el cursor al final en
 * varios navegadores y hace el campo incómodo. La normalización ocurre al SALIR del
 * campo (`normalizarCorreo`, en `validacion.js`).
 */
export const soloCorreo: Saneador = (valor, tope) =>
  recortar(
    texto(valor)
      .trim()
      .normalize('NFD')
      .replaceAll(/[̀-ͯ]/g, '')      // quita las tildes en vez de rechazarlas
      .replaceAll(/[^A-Za-z0-9@._+-]/g, ''),
    tope,
  );

/**
 * Texto libre con tope. No filtra caracteres: solo recorta.
 *
 * Para descripciones clínicas y motivos, donde restringir el juego de caracteres sería
 * contraproducente — ahí se escribe "38,5 °C" y "≤ 24 h".
 */
export const conTope: Saneador = (valor, tope) => recortar(valor, tope);

/**
 * Alfanumérico con separadores de código: lotes, dosis, vías, frecuencias.
 *
 * Admite letras, cifras, espacio y `-_/.,%` porque así se escriben de verdad:
 * `L-2026-0451`, `1 mL/50 kg`, `c/24 h`. Fuera quedan los símbolos que solo entran por
 * error de tecleo o por pegar desde una hoja de cálculo.
 */
export const alfanumerico: Saneador = (valor, tope) =>
  recortar(texto(valor).replaceAll(/[^\p{L}\p{N}\s\-_/.,%°]/gu, ''), tope);

// ---------------------------------------------------------------------------
// Utilidades de fecha y descarga compartidas por las pantallas del Sprint 2.
// Viven aqui —y no repetidas en cada pantalla— porque las cuatro copias que habia
// arrastraban el mismo defecto y se corrigen una sola vez.
// ---------------------------------------------------------------------------

// La fecha de HOY ya no vive aquí (Bug #1086, RN-82): era la del NAVEGADOR, y cerca de la
// medianoche la de Lima y la de México o Chile son días distintos. El hoy de un campo de fecha
// es el del país del caso: `fechaDeHoyEnZona(zona)` en `fechas.ts`.


/** «Recibido desde» posterior a «Recibido hasta»: no hay rango que consultar. */
export const MENSAJE_RANGO_INVERTIDO = 'La fecha «desde» no puede ser posterior a «hasta».';
export const rangoInvertido = (desde?: string, hasta?: string): boolean => Boolean(desde && hasta && desde > hasta);

/**
 * Descarga un `Blob` con el nombre dado.
 *
 * El enlace se AÑADE al DOM antes del clic —Firefox ignora el `click()` de un `<a>`
 * suelto— y se quita después; la URL de objeto se revoca DIFERIDA, porque revocarla
 * en la misma vuelta cancela la descarga en Safari y en algunos Chromium.
 */
export function descargarBlob(blob: Blob, nombre: string): void {
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombre;
  enlace.style.display = 'none';
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
